import {
  CalendarDoc,
  DateUtils,
  DayHours,
  PropertyValues,
  RelationshipLagCalendar,
  ScheduleDoc,
  TaskDoc,
  TaskRelationship,
  UserDoc,
  WorkingType
} from 'common';
import { TaskCreateData } from 'src/components/concourse-schedule/modals/task-create-modal';
import { Calendar } from 'src/components/concourse-schedule/model/Calendar';
import { DependencyType, Task, TreeNodeRelationship } from 'src/components/concourse-schedule/model/Task';
import { ConstraintType } from 'src/schedule/utils/ScheduleCalculate';
import { CalendarUtils } from '../../components/concourse-schedule/utils/CalendarUtils';
import { ConcourseDb, PutResponse } from '../ConcourseDb';
import { authFetch, getAccessToken } from './AuthService';

// Constraint types as stored in TaskDoc

export interface TaskDelta {
  unique_id: number;
  fields: FieldDelta[];
}

export interface FieldDelta {
  field: string;
  old_value: any;
  new_value: any;
}

export class TaskDeltas extends Map<number, FieldDelta[]> {
  constructor() {
    super();
  }
}

export interface TaskChange {
  task_unique_id: number;
  name?: string;
}

export interface TaskNodeChange {
  task_unique_id: number;
  name?: string;
  duration?: number;
  startDate?: Date;
  finishDate?: Date;
  percentDone?: number;
  predecessors?: TaskRelationship[];
  successors?: TaskRelationship[];
}

export interface TaskChange {
  task_unique_id: number;
  name?: string;
}

export interface TaskNodeChange {
  task_unique_id: number;
  duration?: number;
  startDate?: Date;
  finishDate?: Date;
  percentDone?: number;
  predecessors?: TaskRelationship[];
  successors?: TaskRelationship[];
}

export class TaskDocsChangeEvent extends CustomEvent<TaskDoc[]> {
  static readonly TASK_DOCS_CHANGE: string = 'TASK_DOCS_CHANGE';
  constructor(docs: TaskDoc[]) {
    console.log('ScheduleService.TaskDocsChangeEvent constructor', docs);
    super(TaskDocsChangeEvent.TASK_DOCS_CHANGE, { detail: docs });
  }
}

//export type TaskDocType = TaskDoc | RfiDoc | SubmittalDoc;

export class ScheduleService extends EventTarget {
  static readonly TREE_ROOT_ID: string = 'root';
  static readonly RFI_CONTAINER_ID: string = 'rfis';
  static readonly SUBMITTAL_CONTAINER_ID: string = 'submittals';

  // Date-based constraints that require a constraint_date
  static readonly DATE_BASED_CONSTRAINTS: ConstraintType[] = [
    ConstraintType.START_NO_EARLIER_THAN,
    ConstraintType.START_NO_LATER_THAN,
    ConstraintType.FINISH_NO_EARLIER_THAN,
    ConstraintType.FINISH_NO_LATER_THAN,
    ConstraintType.MUST_FINISH_ON,
    ConstraintType.MUST_START_ON,
  ];

  // Color palette for tasks
  static ROOT_TASK_COLORS = [
    '#AD1457',
    '#6A1B9A',
    '#4527A0',
    '#1565C0',
    '#283593',
    '#0277BD',
    '#00838F',
    '#00695C',
    '#2E7D32',
    '#558B2F',
    '#9E9D24',
    '#F9A825',
    '#FF8F00',
    '#EF6C00',
    '#D84315',
    '#4E342E',
    '#424242',
    '#37474F',
  ];

  static getTwentyFourHourCalendar(): Calendar {
    const twentyFourHours: DayHours = {
      type: WorkingType.working,
      hours: [{ from: '00:00', to: '24:00' }],
    }
    return new Calendar({
      _id: "",
      unique_id: -1,
      name: '24 Hour',
      type: "GLOBAL",
      sunday: twentyFourHours,
      monday: twentyFourHours,
      tuesday: twentyFourHours,
      wednesday: twentyFourHours,
      thursday: twentyFourHours,
      friday: twentyFourHours,
      saturday: twentyFourHours,
      exceptions: [],
    });
  }

  static getDefaultScheduleDoc(name: string, user: UserDoc, startDate: Date, finishDate: Date): ScheduleDoc {
    return {
      _id: 'settings',
      custom_fields: [],
      property_values: {
        start_date: startDate.toISOString(),
        status_date: startDate.toISOString(),
        planned_start: startDate.toISOString(),
        currency_symbol: '$',
        project_title: name,
        name: name,
        company: user.company,
        default_calendar_unique_id: 1,
        relationship_lag_calendar: RelationshipLagCalendar.PREDECESSOR,
        current_date: new Date().toISOString(),
        finish_date: finishDate.toISOString(),
        total_slack_calculation_type: 'SMALLEST_SLACK',
        must_finish_by: finishDate.toISOString(),
        author: user.name || user.email || '',
        last_author: user.name || user.email || '',
        last_saved: new Date().toISOString(),
        full_application_name: 'Concourse',
        application_version: '__COMMIT_ID__',
        wbs_code_separator: '.',
      },
      snapshots: [],
      created_date: new Date().toISOString(),
      created_by: user.first_name + ' ' + user.last_name,
    };
  }
  static getDefaultRootTaskDoc(startDate: Date, finishDate: Date, user: UserDoc): TaskDoc {
    return {
      _id: ScheduleService.TREE_ROOT_ID,
      unique_id: 0,
      parent_task_unique_id: null,
      activity_id: null,
      wbs: '0',
      outline_number: '0',
      summary: true,
      name: 'Root Task',
      activity_type: null,
      type: 'FIXED_DURATION',
      duration: 0,
      start: DateUtils.toIso(startDate),
      finish: DateUtils.toIso(finishDate),
      active: true,
      expanded: true,
      calendar_unique_id: 1,
      created_date: DateUtils.toIso(new Date()),
      created_by: user.first_name + ' ' + user.last_name,
      predecessors: null,
      successors: null,
      tags: null,
      rfis: null,
    };
  }

  // ============================================================================
  // DATABASE ACCESS METHODS
  // ============================================================================

  static getSnapshotsDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-snapshots`);
  }

  static getScheduleConfigDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule`);
  }

  static getCalendarDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-calendars`);
  }

  static getTasksDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-tasks`);
  }

  static async processSchedule(projectId: string, s3Key: string): Promise<string> {
    const mpxjUrl = globalThis.ccMPXJBaseUrl + `/mpxj/process-file?projectId=${projectId}&s3Key=${s3Key}`;
    const response = await authFetch(mpxjUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });
    const json = await response.json();

    if (json.error) {
      throw new Error('Error processing schedule: ' + json.error);
    } else if (json.scheduleId) {
      return json.scheduleId;
    } else {
      throw new Error('Unexpected response: ' + JSON.stringify(response));
    }
  }

  static async createScheduleFromScratch(
    projectId: string,
    name: string,
    startDateISO: string,
    finishDateISO: string,
    user: UserDoc,
    description: string = null
  ): Promise<{ message: string }> {

    const calendar = CalendarUtils.getDefaultCalendar();
    const startDate = calendar.getWorkStartOfDay(DateUtils.fromISO(startDateISO));
    const finishDate = calendar.getWorkEndOfDay(DateUtils.fromISO(finishDateISO));
    const createUrl = globalThis.ccLambdaBaseUrl + `/fn/schedules/create?projectId=${projectId}`;
    const response = await authFetch(createUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        scheduleDoc: ScheduleService.getDefaultScheduleDoc(name, user, startDate, finishDate),
        calendarDoc: calendar.doc,
        rootTaskDoc: ScheduleService.getDefaultRootTaskDoc(startDate, finishDate, user),
        description: description,
      }),
    });

    const body: { message?: string; error?: string } = await response.json();

    if (!response.ok) {
      const detail = body.error ?? response.statusText;
      throw new Error(detail);
    }

    if (!body.message) {
      throw new Error('Unexpected response from server when creating schedule.');
    }

    return { message: body.message };
  }

  static async runServerCalculation(projectId: string): Promise<Map<number, FieldDelta[]>> {
    const mpxjUrl = globalThis.ccMPXJBaseUrl + `/mpxj/calculate-schedule?projectId=${projectId}`;
    const response = await authFetch(mpxjUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    });
    const json = await response.json();
    if (json.error) {
      throw new Error('Error calculating schedule: ' + json.error);
    } else if (json.deltas) {
      const deltasObj = JSON.parse(json.deltas);
      const response = new Map<number, FieldDelta[]>();
      Object.entries(deltasObj).forEach(([key, value]) => {
        response.set(Number(key), value as FieldDelta[]);
      });
      return response;
    } else if (json.message) {
      return json.message;
    } else {
      throw new Error('Unexpected response: ' + JSON.stringify(response));
    }
  }

  /**
   * Build a tree structure from flat array of TaskDocs
   * @param tasks - Flat array of TaskDoc
   * @returns Root tasks with children populated
   */
  static parseIntoTree(schedule: ScheduleDoc, taskDocs: TaskDoc[], calendars: Calendar[], defaultCalendar: Calendar): { treeRoot: Task; map: Map<number, Task> } {
    let map: Map<number, Task> = new Map();
    taskDocs.forEach(task => {
      const start = task.actual_start || task.start || task.planned_start;
      const startDate = start ? DateUtils.fromISO(start) : null;

      const finish = task.actual_finish || task.finish || task.planned_finish;
      const finishDate = finish ? DateUtils.fromISO(finish) : null;

      if (!startDate || !finishDate) {
        console.error('Task has no start or finish date', task);
        return;
      }
      const calendar = task.calendar_unique_id ? calendars.find(c => c.id.toString() === task.calendar_unique_id.toString()) : defaultCalendar;
      map.set(task.unique_id, new Task(task, calendar));
    });

    let tree: Task[] = [];
    taskDocs.forEach(doc => {
      const id = doc.unique_id;
      const taskNode = map.get(id);
      if (!taskNode) {
        console.error('Task not found', doc);
        return;
      }

      /* The problem is that the parent_task_unique_id is not the same as the task._id
      so, we need to use the unique_id to find the parent task? or is it the _id? */
      const parentId = doc.parent_task_unique_id;
      const parentNode = map.get(parentId);
      if (parentId != null && parentNode && parentNode != taskNode) {
        parentNode.addChild(taskNode);
      } else {
        tree.push(taskNode);
      }

      doc.predecessors &&
        doc.predecessors.forEach(predecessor => {
          const predecessorId = predecessor.predecessor_task_unique_id || predecessor.task_unique_id;
          const predecessorTask = map.get(predecessorId);
          if (!predecessorTask) {
            console.warn('Predecessor task not found, happens when tree is filtered', predecessorId);
            return;
          }
          if (predecessorTask.id == taskNode.id) {
            console.error(`Trying to add a predecessor to task ${taskNode.id} that is the same as the task`, predecessor);
            return;
          }
          if (!taskNode.predecessors.find(p => p.task == predecessorTask)) {
            taskNode.predecessors.push(new TreeNodeRelationship(predecessorTask, predecessor.type as DependencyType, predecessor.lag));
          }
          if (!predecessorTask.successors.find(s => s.task == taskNode)) {
            predecessorTask.successors.push(new TreeNodeRelationship(taskNode, predecessor.type as DependencyType, predecessor.lag));
          }
        });

      doc.successors &&
        doc.successors.forEach(successor => {
          const successorId = `${successor.successor_task_unique_id || successor.task_unique_id}`;
          const successorTask = map.get(successor.successor_task_unique_id || successor.task_unique_id);
          if (!successorTask) {
            console.warn('Successor task not found, happens when tree is filtered', successorId);
            return;
          }
          if (successorTask.id == taskNode.id) {
            console.error(`Trying to add a successor to task ${taskNode.id} that is the same as the task`, successor);
            return;
          }
          if (!taskNode.successors.find(s => s.task == successorTask)) {
            taskNode.successors.push(new TreeNodeRelationship(successorTask, successor.type as DependencyType, successor.lag));
          }
          if (!successorTask.predecessors.find(p => p.task == taskNode)) {
            successorTask.predecessors.push(new TreeNodeRelationship(taskNode, successor.type as DependencyType, successor.lag));
          }
        });
    });

    // give dates to the containers
    const treeRoot = ScheduleService.getTreeRoot(schedule, defaultCalendar, tree);
    treeRoot.sortDecendents();

    // rfis-container should come first.
    const rfisContainer = taskDocs.find(doc => doc.text1 == 'rfis-container');
    if (rfisContainer) {
      const rfisContainerNode = map.get(rfisContainer.unique_id);
      if (rfisContainerNode) {
        treeRoot.children = treeRoot.children.filter(child => child !== rfisContainerNode);
        treeRoot.children.unshift(rfisContainerNode);
      }
    }
    // submittals-container should come second.
    const submittalsContainer = taskDocs.find(doc => doc.text1 == 'submittals-container');
    if (submittalsContainer) {
      const submittalsContainerNode = map.get(submittalsContainer.unique_id);
      if (submittalsContainerNode) {
        treeRoot.children = treeRoot.children.filter(child => child !== submittalsContainerNode);
        const insertIndex = treeRoot.children.length === 0 ? 0 : 1;
        treeRoot.children.splice(insertIndex, 0, submittalsContainerNode);
      }
    }

    treeRoot.children.forEach((topLevelTask, index) => {
      let color = topLevelTask.color || ScheduleService.ROOT_TASK_COLORS[index % ScheduleService.ROOT_TASK_COLORS.length];
      topLevelTask.setColorIndentLevel(color, 1);
    });
    return { treeRoot, map };
  }

  /**
   * Look for the first level of tasks in the tree
   * Where there is more than one child.
   */
  static getFirstLevelTasks(tree: Task[]): Task[] {
    for (var i = 0; i < 3; i++) {
      if (tree.length == 1) {
        tree = tree[0].children;
      }
    }
    return tree;
  }

  static getTreeRoot(schedule: ScheduleDoc, defaultCalendar: Calendar, tree: Task[]): Task {
    // When we are first creating a schedule, we have only one task and no children.
    if (tree.length == 1 && !tree[0].children.length) {
      return tree[0];
    }

    // 3 attempts to find a root task with more than one child
    // Our tree root must have more than one child for the coloring of the first level of children.
    for (var i = 0; i < 3 && tree.length == 1; i++) {
      if (tree[0].children.length >= 1) {
        // this is our root task, with more than one child.
        return tree[0];
      }
      tree = tree[0].children;
    }

    // If the root task has more than one child, we need to create a new root task.
    if (tree.length >= 1) {
      const root = new Task(
        {
          _id: 'root',
          activity_id: null,
          unique_id: 0,
          parent_task_unique_id: null,
          name: 'Root Task',
          wbs: '0',
          outline_number: '0',
          summary: true,
          type: 'FIXED_DURATION',
          duration: 0,
          start: schedule.property_values.start_date,
          finish: schedule.property_values.finish_date,
          active: true,
          expanded: true,
          calendar_unique_id: 1,
          created_date: new Date().toISOString(),
          created_by: 'system',
          predecessors: null,
          successors: null,
        },
        defaultCalendar,
      );
      tree.forEach(task => {
        root.addChild(task);
      });
      return root;
    }

    return null;
  }

  /**
   * Find a task by ID in a tree structure
   * @param tasks - Array of tasks (can be tree structure)
   * @param taskId - ID to search for
   * @returns Task if found, null otherwise
   */
  static findTaskById(tasks: TaskDoc[], taskId: string): TaskDoc | null {
    for (const task of tasks) {
      if (task._id === taskId) {
        return task;
      }
      if ((task as any).children) {
        const found = this.findTaskById((task as any).children, taskId);
        if (found) return found;
      }
    }
    return null;
  }

  static async saveTaskDoc(projectId: string, doc: TaskDoc): Promise<TaskDoc> {
    const db = this.getTasksDb(projectId);
    const resp: PutResponse = await db.saveDoc(doc);
    return { ...doc, _rev: resp.rev };
  }

  /**
   * Create a child task and add it to the schedule tree without reloading the entire schedule
   * @param projectId - Project ID
   * @param scheduleData - ScheduleData instance
   * @param parentNode - Parent TreeNode (null for root)
   * @param taskData - Task creation data from modal
   * @param userId - User ID who created the task
   * @returns The created TreeNode
   */
  static createTaskDoc(calendar: Calendar, propertyValues: PropertyValues, parent: Task, taskData: TaskCreateData, uniqueId: number, userId: string): TaskDoc {
    // Generate unique IDs
    //const uniqueId = scheduleData.getNextUniqueId();
    console.log('ScheduleService.createTaskDoc: uniqueId', uniqueId);
    const docId = ConcourseDb.makeShortDocId();

    // Calculate WBS code based on parent WBS and child index
    const wbsCodeSeparator = propertyValues.wbs_code_separator || '.';
    const childNum = `${parent.children.length + 1}`;
    const wbs = parent.wbs === '0' ? childNum : `${parent.wbs}${wbsCodeSeparator}${childNum}`;
    
    // Create TaskDoc
    const startDate = calendar.getWorkStartOfDay(taskData.startDate);
    const finishDate = calendar.getDate(startDate, taskData.duration); // preview only....

    const finishDateISO = DateUtils.toIso(finishDate);

    // Apply color based on parent's color scheme
    const color = parent.color ? Task.shadeParentColor(parent.color, parent.level + 1) : ScheduleService.ROOT_TASK_COLORS[parent.children.length];

    return {
      _id: docId,
      unique_id: uniqueId,
      parent_task_unique_id: parent.uniqueId,
      calendar_unique_id: calendar.id,
      activity_id: null,
      name: taskData.name,
      wbs: wbs,
      outline_number: wbs,
      summary: false,
      type: 'FIXED_UNITS',

      constraint_type: ConstraintType.START_NO_EARLIER_THAN,
      constraint_date: DateUtils.toIso(taskData.startDate),
      start: DateUtils.toIso(taskData.startDate),
      finish: finishDateISO,
      duration: taskData.duration,
      actual_duration: 0,
      remaining_duration: taskData.duration,

      predecessors: [],
      successors: [],
      tags: [],
      rfis: [],

      active: true,
      expanded: true,

      text3: color,

      created_date: new Date().toISOString(),
      created_by: userId,
    };
/*
    // Add to tasks array
    scheduleData.tasks.push(savedTaskDoc);

    // Create TreeNode
    const taskNode = new Task(savedTaskDoc, calendar);

    // Add to taskMap
    scheduleData.taskMap.set(uniqueId, taskNode);

    // Add to parent or root
    if (parentNode) {
      parentNode.addChild(taskNode);
    } else {
      scheduleData.treeRoot.addChild(taskNode);
    }

    // Sort children after adding
    if (parentNode) {
      parentNode.sortDecendents();
    } else {
      scheduleData.treeRoot.sortDecendents();
    }



    // Setup event listener for the new node
    //ScheduleService.setupTreeNodeListener(projectId, scheduleData, taskNode);

    // Dispatch change event
    scheduleData.dispatchEvent(new Event(ScheduleData.REBUILD));
    console.log('ScheduleService.createChildTask: taskNode.name', taskNode.name, 'taskNode.id', taskNode.id, 'taskNode', taskNode);
    return taskNode;
    */
  }

  static async saveTasks(projectId: string, docs: TaskDoc[], awaitSync: boolean = false): Promise<TaskDoc[]> {
    const db = this.getTasksDb(projectId);
    const resp: PutResponse[] = await db.bulkDocs(docs.map(doc => ({ ...doc, updated_date: new Date().toISOString() })));
    if (awaitSync) {
      await db.awaitSync();
    }
    return resp.map(r => ({ ...docs.find(d => d._id === r.id), _rev: r.rev }));
  }

  static async deleteTask(projectId: string, doc: TaskDoc, awaitSync: boolean = false): Promise<void> {
    const db = this.getTasksDb(projectId);
    await db.remove(doc);
    if (awaitSync) {
      await db.awaitSync();
    }
  }

  // ============================================================================
  // CALENDAR METHODS
  // =========================  ===================================================

  static async getCalendarDoc(projectId: string, calendarId: string): Promise<CalendarDoc | null> {
    const db = this.getCalendarDb(projectId);
    return await db.getDoc(calendarId);
  }

  static async getCalendarOrDefault(projectId: string, calendarUniqueId: number): Promise<Calendar> {
    const docs = await this.getCalendarDocs(projectId);
    const calendarDoc = docs.find(doc => doc.unique_id === calendarUniqueId);
    if (calendarDoc) {
      return new Calendar(calendarDoc);
    }
    return CalendarUtils.getDefaultCalendar();
  }

  static async getCalendarDocs(projectId: string): Promise<CalendarDoc[]> {
    const db = this.getCalendarDb(projectId);
    return await db.getDocs();
  }

  static async saveCalendar(projectId: string, doc: CalendarDoc): Promise<PutResponse> {
    const db = this.getCalendarDb(projectId);
    const resp = await db.saveDoc(doc);
    await db.awaitSync();
    return resp;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get the earliest start date from an array of tasks
   */
  static getEarliestStartDate(tasks: TaskDoc[]): Date | null {
    const dates = tasks
      .map(t => t.actual_start || t.start || t.planned_start)
      .filter(d => !!d)
      .map(d => new Date(d).getTime());
    return dates.length > 0 ? new Date(Math.min(...dates)) : null;
  }

  /**
   * Get the latest end date from an array of tasks
   */
  static getLatestEndDate(tasks: TaskDoc[]): Date | null {
    const dates = tasks
      .map(t => t.actual_finish || t.finish || t.planned_finish)
      .filter(d => !!d)
      .map(d => new Date(d).getTime());
    return dates.length > 0 ? new Date(Math.max(...dates)) : null;
  }

  /**
   * Sort tasks by start date
   */
  static sortByDate(a: Task, b: Task): number {
    const startA = a.startDate.getTime();
    const startB = b.startDate.getTime();
    if (startA !== startB) {
      return startA - startB;
    } else {
      return a.finishDate.getTime() - b.finishDate.getTime();
    }
  }

  /**
   * Filter tasks by date range
   */
  static filterByDateRange(tasks: TaskDoc[], startDate: Date, finishDate: Date): TaskDoc[] {
    return tasks.filter(task => {
      const taskStart = DateUtils.fromISO(task.actual_start || task.start || task.planned_start);
      const taskFinish = DateUtils.fromISO(task.actual_finish || task.finish || task.planned_finish);
      return taskStart <= finishDate && taskFinish >= startDate;
    });
  }

  /**
   * Filter tasks by search term (searches name, wbs, activity_id)
   */
  static filterBySearchTerm(tasks: TaskDoc[], term: string): TaskDoc[] {
    if (!term) return tasks;
    const lowerTerm = term.toLowerCase();
    return tasks.filter(task => task.name?.toLowerCase().includes(lowerTerm) || task.wbs?.toLowerCase().includes(lowerTerm) || task.activity_id?.toLowerCase().includes(lowerTerm));
  }

  static getTaskAncestry(task: Task): Task[] {
    const ancestry: Task[] = [];
    while (task && task.parent) {
      ancestry.push(task.parent);
      task = task.parent;
    }
    return ancestry.reverse();
  }

  static getTaskDocAncestry(tasks: TaskDoc[], task: TaskDoc): TaskDoc[] {
    const ancestry: TaskDoc[] = [];
    while (task && task.parent_task_unique_id) {
      const parent = tasks.find(t => t.unique_id === task.parent_task_unique_id);
      if (parent) {
        ancestry.push(parent);
        task = parent;
      } else {
        break;
      }
    }
    return ancestry.reverse();
  }

  /**
   * Get leaf tasks (tasks with no children and not summary/container tasks)
   * @param tasks - Flat array of TaskDoc
   * @returns Array of leaf TaskDoc (tasks with no children)
   */
  static getLeafTasksDoc(tasks: TaskDoc[]): TaskDoc[] {
    // Create a set of all parent task IDs for quick lookup
    const parentIds = new Set<number>();
    tasks.forEach(task => {
      if (task.parent_task_unique_id) {
        parentIds.add(task.parent_task_unique_id);
      }
    });

    // Filter tasks that are not parents and not summary tasks
    return tasks.filter(task => {
      // Not a parent (no other task references this as parent)
      const isNotParent = !parentIds.has(task.unique_id);

      // Not a summary/container task
      const isNotSummary = !task.summary;

      return isNotParent && isNotSummary;
    });
  }

  static getLeafNodes(task: Task): Task[] {
    if (task.isLeaf) {
      return [task];
    } else {
      return task.children.flatMap(child => this.getLeafNodes(child));
    }
  }

  /**
   * Filter task tree by date range (inclusive)
   * Returns tasks that overlap with the given date range, preserving tree structure
   * @param tasks - Array of Task objects (tree structure)
   * @param startDate - Start of date range
   * @param finishDate - End of date range
   * @returns Filtered array of Task objects with tree structure preserved
   */
  static filterTreeByDateRange(tasks: Task[], startDate: Date, finishDate: Date): Task[] {
    const filterTask = (task: Task): boolean => {
      // For containers (RFI/Submittal containers), check if they should be included
      if (task.isContainer) {
        // Include container if any of its children overlap with date range
        return task.children.some(child => filterTask(child));
      }

      // Use the Task's Date properties directly (already parsed)
      const taskStart = task.startDate;
      const taskFinish = task.finishDate;

      // Check if task overlaps with the date range
      // Task overlaps if it starts before the range ends AND finishes after the range starts
      return taskStart <= finishDate && taskFinish >= startDate;
    };

    // Recursively filter tasks and their children
    const filterWithChildren = (task: Task): Task | null => {
      const matches = filterTask(task);

      if (task.children.length > 0) {
        // Filter children recursively
        const filteredChildren = task.children.map(child => filterWithChildren(child)).filter(child => child !== null) as Task[];

        // If this task matches OR has matching children, include it
        if (matches || filteredChildren.length > 0) {
          // Create a shallow copy with filtered children
          const filtered = Object.assign(Object.create(Object.getPrototypeOf(task)), task);
          filtered.children = filteredChildren;
          return filtered;
        }
      } else if (matches) {
        // Leaf task that matches
        return task;
      }

      return null;
    };

    return tasks.map(task => filterWithChildren(task)).filter(task => task !== null) as Task[];
  }

  static async takeSnapshot(projectId: string): Promise<{ message: string; success: boolean; snapshotId: string }> {
    const mpxjUrl = globalThis.ccMPXJBaseUrl + `/mpxj/snapshot-schedule?projectId=${projectId}`;
    return await authFetch(mpxjUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    })
      .then(response => response.json());
  }

  static async restoreSnapshot(projectId: string, snapshotId: string): Promise<{ message: string; success: boolean; snapshotId: string }> {
    const mpxjUrl = globalThis.ccMPXJBaseUrl + `/mpxj/restore-snapshot?projectId=${projectId}&snapshotId=${snapshotId}`;
    return await authFetch(mpxjUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    })
      .then(response => response.json())
      .catch(err => {
        console.error(err);
      });
  }

  static async exportSchedule(projectId: string, format: string): Promise<{ message: string; s3Key: string; s3Bucket: string; fileFormat: string; extension: string }> {
    const mpxjUrl = globalThis.ccMPXJBaseUrl + `/mpxj/export-schedule?projectId=${projectId}&fileFormat=${format}`;
    return await authFetch(mpxjUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
    }).then(response => response.json());
  }
/*
  static createTaskDoc(calendar:Calendar, startDate:Date, duration:number, parent:Task, uniqueId:number, wbs:string, name:string, userId:string):TaskDoc {
    const finishDate = calendar.getDate(startDate, duration); // preview only....
    return {
      _id: ConcourseDb.makeShortDocId(),
      unique_id: uniqueId,
      parent_task_unique_id: parent.doc.unique_id,
      calendar_unique_id: calendar.doc.unique_id,
      activity_id: null,
      name: name,
      wbs: wbs,
      summary: false,
      type: 'FIXED_UNITS',

      constraint_type: ConstraintType.START_NO_EARLIER_THAN,
      constraint_date: DateUtils.toIso(startDate),
      start: DateUtils.toIso(startDate),
      finish: DateUtils.toIso(finishDate),
      duration: duration,
      actual_duration: 0,
      remaining_duration: duration,

      predecessors: [],
      successors: [],
      tags: [],
      rfis: [],

      active: true,
      expanded: true,
      created_date: new Date().toISOString(),
      created_by: userId,
    };
  }
    */

  static getRootTaskDoc(tasks:TaskDoc[]): TaskDoc {
    const uniuqueIds = tasks.map(doc => doc.unique_id);
    return tasks.find(doc => doc.parent_task_unique_id === null || !uniuqueIds.includes(doc.parent_task_unique_id));
  }
}
